import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu, Layers, Wifi, Terminal, Package,
  Check, ArrowRight
} from 'lucide-react';
import apiClient from '../config/axios';
import './Services.css';

const iconMap = {
  'microchip': Cpu,
  'circuit-board': Layers,
  'cpu': Cpu,
  'wifi': Wifi,
  'terminal': Terminal,
  'box': Package
};

const serviceDetails = {
  'Firmware Development': [
    'Microcontroller programming (ARM, AVR, PIC, STM32)',
    'RTOS development and integration',
    'Low-power optimization',
    'Communication protocols (SPI, I2C, UART, CAN)',
    'Bootloader development',
    'Firmware updates over-the-air (OTA)'
  ],
  'Hardware Design': [
    'Schematic design and review',
    'Component selection and BOM optimization',
    'Design for manufacturability (DFM)',
    'Signal integrity analysis',
    'Power management design',
    'EMC/EMI compliance'
  ],
  'PCB Design': [
    'Multi-layer PCB design',
    'High-speed digital design',
    'RF and antenna design',
    'Flex and rigid-flex PCB',
    'Design rule check (DRC)',
    'Gerber file generation'
  ],
  'IoT Solutions': [
    'Cloud connectivity (AWS, Azure, GCP)',
    'Wireless protocols (WiFi, BLE, LoRa, Zigbee)',
    'Edge computing solutions',
    'Real-time data analytics',
    'Remote monitoring dashboards',
    'OTA updates and device management'
  ],
  'Embedded Linux': [
    'BSP development and customization',
    'Linux kernel configuration',
    'Device driver development',
    'Yocto/Buildroot integration',
    'System optimization and hardening',
    'Application development'
  ],
  'Product Development': [
    'Concept to prototype',
    'Industrial design collaboration',
    'Certification support (CE, FCC, UL)',
    'Manufacturing support',
    'Testing and validation',
    'Documentation and training'
  ]
};

const Services = () => {
  const [services, setServices] = useState([]);

  const fetchServices = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/services');
      setServices(response.data.data || getDefaultServices());
    } catch (error) {
      setServices(getDefaultServices());
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const getDefaultServices = () => [
    { id: 1, title: 'Firmware Development', description: 'We specialize in firmware development that plays a vital role in embedded products. Our expertise focuses on reducing hardware costs, improving battery efficiency, and optimizing system performance for your specific requirements.', icon: 'microchip' },
    { id: 2, title: 'Hardware Design', description: 'Leveraging extensive product experience across multiple application domains, we create optimal schematic designs tailored to your requirements. Our hardware solutions ensure reliability and cost-effectiveness.', icon: 'circuit-board' },
    { id: 3, title: 'PCB Design', description: 'Our proficiency with multiple PCB design tools enables easy upgrades and modifications to existing designs. We deliver high-quality PCB layouts that meet industry standards and your specifications.', icon: 'cpu' },
    { id: 4, title: 'IoT Solutions', description: 'We develop comprehensive IoT solutions that bridge the gap between devices and cloud connectivity. Our IoT data bridges enable seamless data transfer and real-time monitoring capabilities.', icon: 'wifi' },
    { id: 5, title: 'Embedded Linux', description: 'Custom embedded Linux development for industrial and consumer applications. We provide BSP development, driver integration, and system optimization services.', icon: 'terminal' },
    { id: 6, title: 'Product Development', description: 'End-to-end product development services from concept to production. We handle prototyping, testing, certification support, and manufacturing assistance.', icon: 'box' }
  ];

  const renderIcon = (iconName, size = 40) => {
    const IconComponent = iconMap[iconName] || Cpu;
    return <IconComponent size={size} />;
  };

  return (
    <div className="services-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Our Services</h1>
          <p>Comprehensive embedded solutions tailored to your business needs</p>
        </div>
      </section>

      {/* Services List */}
      <section className="services-list section">
        <div className="container">
          {services.map((service, index) => (
            <div key={service.id} className={`service-detail ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="service-content">
                <div className="service-icon-large">
                  {renderIcon(service.icon)}
                </div>
                <h2>{service.title}</h2>
                <p>{service.description}</p>
                <ul className="service-features">
                  {(serviceDetails[service.title] || []).map((feature, idx) => (
                    <li key={idx}>
                      <Check size={18} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="service-visual">
                <div className="visual-placeholder">
                  {renderIcon(service.icon, 80)}
                  <span>{service.title}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section bg-light">
        <div className="container">
          <div className="section-title">
            <h2>Our Process</h2>
            <p>How we deliver exceptional results</p>
          </div>
          <div className="process-grid">
            <div className="process-step">
              <span className="step-number">01</span>
              <h3>Discovery</h3>
              <p>We understand your requirements, goals, and constraints through detailed discussions.</p>
            </div>
            <div className="process-step">
              <span className="step-number">02</span>
              <h3>Design</h3>
              <p>Our team creates detailed designs and specifications for your approval.</p>
            </div>
            <div className="process-step">
              <span className="step-number">03</span>
              <h3>Development</h3>
              <p>We build your solution using best practices and industry standards.</p>
            </div>
            <div className="process-step">
              <span className="step-number">04</span>
              <h3>Testing</h3>
              <p>Rigorous testing ensures your product meets all quality requirements.</p>
            </div>
            <div className="process-step">
              <span className="step-number">05</span>
              <h3>Delivery</h3>
              <p>We deliver the final product with documentation and support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Project?</h2>
            <p>Contact us today to discuss your requirements and get a free consultation.</p>
            <Link to="/contact" className="btn btn-primary">
              Get in Touch <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
